<script>
export default {
    name: 'OrderScan' 
}
</script>

<script setup>
import url from '@/config/urls';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter, onBeforeRouteLeave } from 'vue-router';
import { modalUtil } from '@functions/modalUtil';
import axios from 'axios';
import { apiError } from '@functions/apiError.js';
import { useBarcodeScanner } from '@/services/useBarcodeScanService';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import DeleteFromBarcodeScanConfirmModal from '@parts/modal/DeleteFromBarcodeScanConfirmModal.vue';
import Goods from '@parts/Goods.vue';
import IdenticalFormulationItemAppModal from '@parts/modal/IdenticalFormulationItemAppModal.vue';
import NoTargetGoodsModal from '@parts/modal/NoTargetGoodsModal.vue';
import AddToCartConfirmModal from "@parts/modal/AddToCartConfirmModal.vue";
import NoAddCartConfirmModal from '@parts/modal/NoAddCartConfirmModal.vue';
import DiscontinuedLeaveConfirmModal from '@parts/modal/DiscontinuedLeaveConfirmModal.vue';
import AddToCartAppCompleteModal from '@parts/modal/AddToCartAppCompleteModal.vue';
import { computeJan13CheckDigit } from '@functions/barcodeScan';
import { useServiceApplyStore } from '@stores/serviceApply';
import { useAuthStore } from '@stores/auth';

// 画面ID
const screenId = ref("OrderScan");

const props = defineProps({
    topPageUrl: {
        type: String,
        default: url.page.top
    }
    , loginUrl: {
        type: String,
        default: url.login
    }
    , orderCartCountApiUrl: {
        type: String
        , default: url.api.orderCart.count
    }
    , identicalFormulationListApiUrl: {
        type: String,
        default: url.api.identicalFormulationList
    }
    , orderSearchApiUrl: {
        type: String,
        default: url.api.orderSearch
    }
    , orderCartAddApiUrl: {
        type: String,
        default: url.api.orderCart.add
    }
    , licenseApiUrl: {
        type: String,
        default: url.api.qrDecoderNeo.license
    }
    , loadingIcon: {
        type: String
        , default: url.static.loadingIcon
    }
    , goodsTypesApiUrl: {
        type: String,
        default: url.api.goodsTypes
    }
    , serviceApplyGetApiUrl: {
        type: String,
        default: url.api.serviceApply.get
    }
});

const router = useRouter();
const { showModal, hideModal } = modalUtil();

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
    isLoading.value = loading;
}

// ローディング
const isLoading = ref(false);

// サービス利用状況
const serviceApplyStore = useServiceApplyStore();
const barcodeScan = computed(() => serviceApplyStore.barcodeScan);
const auth = useAuthStore();

// スキャンデータ
const cartTotalRecord = ref(0);
const scannedItem = ref(null);
const goodsTypes = ref([]);
const operatingGoodsData = ref(null);
const identicalFormulationJanCode = ref(null);
// 同一成分モーダル選択時に、表示中商品をカート追加するか
// true: 調剤包装単位コード読取から開いた場合 / false: 同一成分検索ボタンから開いた場合
const identicalModalAddsPreviousToCart = ref(false);

const cartTotalQuantity = ref(0);

// 削除前数量保持
const previousQuantity = ref(1);

// カートに追加確認フラグ
const isConfirmedAdd = ref(false);

// 遷移許可フラグ
const allowLeave = ref(false);

// 次ページ遷移フラグ
const nextRoute = ref(null);

// ページ遷移の保留フラグ
const pendingNavigation = ref(null);

// 取扱中止品残存時の確認ダイアログ遷移先コンテキスト（'cart' / 'back'）
const discontinuedLeaveTo = ref('cart');

// スキャンによる自動登録かカートに追加ボタン押下による手動登録か
const isManualAdd = ref(false);

// エラー処理
const handleApiError = (error) => {
	apiError(error, errorMessages);
};

const fetchGoodsTypes = async() => {
    try {
        const response = await axios.get(props.goodsTypesApiUrl);
        goodsTypes.value = response?.data?.response?.goodsTypes || [];
    } catch (error) {
        apiError(error, errorMessages);
    }
};

const countCartGoods = async() => {
    try {
        const response = await axios.get(props.orderCartCountApiUrl);
        cartTotalRecord.value = parseInt(response?.data?.response?.totalNum ?? 0, 10);
        cartTotalQuantity.value = parseInt(response?.data?.response?.totalQuantity ?? 0, 10);
    } catch (error) {
        apiError(error, errorMessages);
    }
};

// 商品をカートに追加するAPI呼び出し
const addToCart = async(operatingGoods = null)=> {
    const targetGoods = operatingGoods || scannedItem.value;
    if (!targetGoods) return;

    const janCode = targetGoods.janCode;
    const quantity = targetGoods.quantity;

    handleUpdateLoading(true);
    try {
        await axios.post(props.orderCartAddApiUrl, {
            goodsList: [
                {
                janCode
                , quantity
                }
            ]
        });
        // 手動追加時はここでクリア
        if (isManualAdd.value) {
            clearScannedData();
            isManualAdd.value = false;
        }
        await countCartGoods();
        // 完了モーダル表示
        showModal('addToCartAppCompleteModal');
    } catch(error) {
        apiError(error, errorMessages);
    } finally {
        handleUpdateLoading(false);
    };
};

// 商品データからスキャン表示用オブジェクトを生成
const createScannedItem = (goods) => ({
    janCode: goods.janCode
    , stockSymbol: goods.stockSymbol
    , makerName: goods.makerName
    , formalGoodsName: goods.formalGoodsName
    , attentionLabel: goods.attentionLabel
    , previousOrderDate: goods.previousOrderDate
    , previousOrderQuantity: goods.previousOrderQuantity
    , canOrder: goods.canOrder ?? true
    , quantity: 1
    , salePackagingUnitCode: goods.salePackagingUnitCode
    , dispensingPackagingUnitCode: goods.dispensingPackagingUnitCode
    , goodsType: goods.goodsType
    , generic: goods.generic
    , japanesePharmacopoeia: goods.japanesePharmacopoeia
    , powerfulDrug: goods.powerfulDrug
});

// 前回スキャン商品が発注可能な場合、カート追加を試行する
const tryAddPreviousScannedItemToCart = async(newJanCode) => {
    if (scannedItem.value && newJanCode !== scannedItem.value.janCode && scannedItem.value.canOrder) {
        return await tryAddToCart();
    }
    return true;
};

// スキャン結果の商品を画面にセット
const applyScannedGoods = async(goods) => {
    const canProceed = await tryAddPreviousScannedItemToCart(goods.janCode);
    if (!canProceed) {
        return false;
    }

    if ((goods.canOrder ?? true) === false) {
        errorMessages.value = ['発注不可商品がスキャンされました'];
        showModal('apiErrorModal');
    }

    scannedItem.value = createScannedItem(goods);
    return true;
};

const addScannedItem = async(product) => {
    return applyScannedGoods(product.goodsList[0]);
};

// スキャン結果をもとに商品検索・分岐処理
const handleScanResult = async(codemark, codedata, { playFoundSound, playNotFoundSound } = {}) => {
    try {

        // ローディング中の場合、中断
        if(isLoading.value) {
            return;
        }

        // A0: JANコード, B0: 短縮JANコード
        if (codemark === "A0" || codemark === "B0") {
            // 13桁になるように先頭ゼロ埋め
            const janCode = codedata.padStart(13, '0');

            // スキャン済み商品と同一の場合中断
            if (scannedItem.value && janCode === scannedItem.value.janCode) {
                await playFoundSound?.();
                scannedItem.value.quantity += 1;
                return;
            }

            isLoading.value = true;
            const res = await axios.get(props.orderSearchApiUrl, {
                params: {
                    janCode: janCode
                    , page: 1
                    , size: 1
                    , includeDiscontinuedGoods: true
                }
            });
            
            if (res.data?.response?.totalNum >= 1) {
                await playFoundSound?.();
                const isScannedAdd = await addScannedItem(res.data.response);
                if(!isScannedAdd) {
                    return;
                }
            } else {
                await playNotFoundSound?.();
                showModal('noTargetGoodsModal');
            }
        // GS1コード処理
        } else if(codemark === "R0" || codemark === "V0") {

            // codedataの先頭01を除去し、14桁を取得
            const gtin = codedata.replace(/^01/, '').substring(0, 14);

            // 調剤包装単位コード(GTIN先頭が0)
            if (gtin?.startsWith("0")) {

                // スキャン済み商品と同一の場合中断
                if (scannedItem.value && gtin === scannedItem.value.dispensingPackagingUnitCode) {
                    await playFoundSound?.();
                    scannedItem.value.quantity += 1;
                    return;
                }

                isLoading.value = true;

                const res = await axios.get(props.orderSearchApiUrl, {
                    params: {
                        dispensingPackagingUnitCode: gtin
                        , page: 1
                        , size: 1
                        , includeDiscontinuedGoods: true
                    }
                });

                const list = res.data?.response?.goodsList ?? [];

                if (list.length === 0) {
                    await playNotFoundSound?.();
                    showModal('noTargetGoodsModal');
                    return;
                }

                await playFoundSound?.();
                const janCode = list[0]?.janCode;
                identicalFormulationJanCode.value = janCode;
                // 表示中商品があれば選択時にカート追加する
                identicalModalAddsPreviousToCart.value = true;

                // 同一成分モーダル表示（一覧はモーダル内で取得）
                showModal('identicalFormulationItemAppModal');
            
            } else {

                const first12 = gtin.substring(1, 13);

                // 12桁にチェックデジットを付与しJANコードを算出
                const janCode = first12 + computeJan13CheckDigit(first12);

                // スキャン済み商品と同一の場合中断
                if (scannedItem.value && janCode === scannedItem.value.janCode) {
                    await playFoundSound?.();
                    scannedItem.value.quantity += 1;
                    return;
                }

                isLoading.value = true;

                const res = await axios.get(props.orderSearchApiUrl, {
                    params: {
                        janCode: janCode
                        , page: 1
                        , size: 1
                        , includeDiscontinuedGoods: true
                    }
                });

                const list = res.data?.response?.goodsList ?? [];

                if (list.length === 0) {
                    await playNotFoundSound?.();
                    showModal('noTargetGoodsModal');
                    return;
                }

                await playFoundSound?.();
                const isScannedAdd = await addScannedItem({ goodsList: list });
                if(!isScannedAdd) {
                    return;
                }
            }
        // 不正バーコード
        } else {
            // バーコードが読み取れたが商品がない
            await playNotFoundSound?.();
            showModal('noTargetGoodsModal');
        }
    } catch(error) {
        apiError(error, errorMessages);
    } finally {
        isLoading.value = false;
    }
}

// 同一成分検索モーダルを閉じる処理
const handleIdenticalModalClosed = () => {
    identicalFormulationJanCode.value = null;
    identicalModalAddsPreviousToCart.value = false;
}

// 同一成分検索（一覧はモーダル内で取得）
const searchSameIngredients = () => {
    identicalFormulationJanCode.value = scannedItem.value.janCode;
    // ボタン経由では表示中商品をカート追加しない（選択商品の表示のみ）
    identicalModalAddsPreviousToCart.value = false;
    showModal('identicalFormulationItemAppModal');
}

// 同一成分検索可能かどうか
const isIdenticalDisabled = computed(() => {
    return (
        !scannedItem.value
        || !scannedItem.value.salePackagingUnitCode
    )
});

const isAddToCartDisabled = computed(() => {
    return (
        !scannedItem.value
        || !scannedItem.value.canOrder
    );
});

// 同一成分検索して選択押下時に、スキャン商品として設定する
const handleSelectDrug = async(drug) => {
    // hideModal により hidden.bs.modal → handleIdenticalModalClosed が走るため、先に退避する
    const addPreviousToCart = identicalModalAddsPreviousToCart.value;
    identicalModalAddsPreviousToCart.value = false;
    hideModal('identicalFormulationItemAppModal');

    if (addPreviousToCart) {
        if (scannedItem.value && drug.janCode === scannedItem.value.janCode) {
            // 表示中商品と同一の場合、数量を加算（スキャン時と同様）
            scannedItem.value.quantity += 1;
            return;
        }
        // 表示中商品と異なる場合、表示中商品をカート追加してから選択商品を表示
        await applyScannedGoods(drug);
        return;
    }

    // 同一成分検索ボタン: カート追加せず選択商品のみ表示
    if ((drug.canOrder ?? true) === false) {
        errorMessages.value = ['発注不可商品がスキャンされました'];
        showModal('apiErrorModal');
    }
    scannedItem.value = createScannedItem(drug);
}

const setOperatingGoodsData = (goodsData) => {
    operatingGoodsData.value = goodsData;
};

const handleGoodsAddToCart = async() => {
    if (!operatingGoodsData.value) return;
    await addToCart(operatingGoodsData.value);
};

// カートに追加する場合はモーダルを閉じ、カートに追加
const handleConfirmAddToCart = async() => {
    isConfirmedAdd.value = true;
    const modalEl = document.getElementById('addToCartConfirmModal');
    const modal = bootstrap.Modal.getInstance(modalEl);

    if (modal) {
        modal.hide();
    }
    await addToCart();
}

// カート追加前確認後、カートへ追加
const tryAddToCart = async(manual = false) => {
    if (!scannedItem.value || !scannedItem.value.canOrder) return false;
    const janCode = scannedItem.value.janCode;

    isManualAdd.value = manual;

    handleUpdateLoading(true);
    try {
        const resGoodsCount = await axios.get(props.orderCartCountApiUrl, {
            params: {
                janCodeList: [janCode]
            }
        });
        if (resGoodsCount.data.response.totalNum === 0) {
            await addToCart();
            return true;
        } else {
            handleUpdateLoading(false);
            isConfirmedAdd.value = false;
            showModal('addToCartConfirmModal');
            return await waitModalClose();
        }
    } catch(error) {
        apiError(error, errorMessages);
    } finally {
        handleUpdateLoading(false);
    }
    
}

// 数量バリデーションエラー
const handleValidationError = (janCode, hasError, quantityData) => {
    // エラーなしなら何もしない
    if (!hasError || !quantityData) {
        return;
    }
    previousQuantity.value = quantityData.previousQuantity;
    if (Number(quantityData.currentQuantity) <= 0) {
        showModal('deleteFromBarcodeScanConfirmModal');
    }
}
// Goods側で手入力された数量をscannedItemに反映する
const handleQuantityChanged = (janCode, quantity) => {
    if (scannedItem.value && scannedItem.value.janCode === janCode) {
        scannedItem.value.quantity = quantity;
    }
};
// スキャン商品削除キャンセル
const cancelDeleteFromBarcodeScan = () => {
    if (!scannedItem.value) return;

    scannedItem.value.quantity = previousQuantity.value
    scannedItem.value = { ...scannedItem.value };
    hideModal('deleteFromBarcodeScanConfirmModal');
}

// スキャン商品 削除
const deleteFromBarcodeScan = () => {
    if (!scannedItem.value) return;

    // 商品を削除
    scannedItem.value = null;
}

// モーダルで選択したボタンによって制御を分岐
const waitModalClose = () => {
    return new Promise((resolve) => {
        const modalEl = document.getElementById('addToCartConfirmModal');

        if(!modalEl) {
            resolve(false);
            return;
        }

        const modalHandler = () => {
            modalEl.removeEventListener('hidden.bs.modal', modalHandler);

            if(isConfirmedAdd.value) {
                resolve(true);
            } else {
                resolve(false);
            }
        };
        modalEl.addEventListener('hidden.bs.modal', modalHandler);
    })
}

// 完了画面を閉じたらスキャン商品をクリアし、ページ遷移先に移動する
const handleCompleteModalHidden = () => {

    if (pendingNavigation.value === 'back') {
        pendingNavigation.value = null;
        router.back();
        return;
    }

    if (pendingNavigation.value) {
        router.push(pendingNavigation.value);
        pendingNavigation.value = null;
    }
};

// スキャンデータをクリア
const clearScannedData = () => {
    scannedItem.value = null;
};

// カートに追加して画面遷移
const handleAddAndLeave = async() => {
    allowLeave.value = true;
    pendingNavigation.value = nextRoute.value || 'back';

    const result = await tryAddToCart();

    if (!result) {
        allowLeave.value = false;
        pendingNavigation.value = null;
        return;
    }
};

// カートに追加せず画面遷移
const handleLeaveWithoutAdd = async() => {
    allowLeave.value = true;
    await stopCameraPreview();

    if (nextRoute.value?.fullPath) {
        await router.push(nextRoute.value.fullPath);
    } else if (nextRoute.value?.path) {
        await router.push(nextRoute.value.path);
    } else {
        router.back();
    }
};

// 取扱中止品残存時：追加せず画面遷移（カートへ／前画面へ）
// 取扱中止品は追加しないため tryAddToCart は呼ばず、scannedItem もクリアしない。
// 離脱前処理は handleLeaveWithoutAdd を踏襲する。
const handleDiscontinuedProceed = async() => {
    await handleLeaveWithoutAdd();
};

// 戻るボタン押下時の処理
// 未追加商品があれば確認モーダル表示
const handleBeforeLeave = async() => {
    if (!scannedItem.value) {
        await stopCameraPreview();
        router.back();
        return;
    }

    nextRoute.value = 'back';
    // 取扱中止品が残存している場合は専用の確認ダイアログを表示
    if (scannedItem.value.canOrder === false) {
        discontinuedLeaveTo.value = 'back';
        showModal('discontinuedLeaveConfirmModal');
        return;
    }
    showModal('noAddCartConfirmModal');
};

// 画面離脱ガード
// 未追加の商品がある場合は遷移を止める。離脱時はカメラを停止する。
onBeforeRouteLeave(async(to) => {
    if (!allowLeave.value && scannedItem.value) {
        nextRoute.value = to;
        // 取扱中止品が残存している場合は専用の確認ダイアログを表示
        if (scannedItem.value.canOrder === false) {
            discontinuedLeaveTo.value = 'cart';
            showModal('discontinuedLeaveConfirmModal');
            return false;
        }
        showModal('noAddCartConfirmModal');
        return false;
    }

    await stopCameraPreview();
    return true;
});

// 共通スキャンサービス
const {
    cameraAreaRef
    , videoRef
    , canvasRef
    , scanError
    , isReading
    , isScanSuccessHighlight
    , errorMessages
    , fetchLicense
    , startCameraPreview
    , stopCameraPreview
    , stopRotationWatcher
    , startScan
} = useBarcodeScanner({
    loginUrl: props.loginUrl
    , licenseApiUrl: props.licenseApiUrl
    , onScanResult: handleScanResult,
})

onMounted(async() => {

    await fetchGoodsTypes();
    await countCartGoods();
    await fetchLicense();
    await startCameraPreview();
    await serviceApplyStore.fetchServiceApply(props.serviceApplyGetApiUrl);

    if (!barcodeScan?.value?.isAvailable || auth.user?.barcodeScanApplyPermissionFlg !== 1) {
        router.push(props.topPageUrl);
    }
});

onBeforeUnmount(async() => {
    stopRotationWatcher();
    await stopCameraPreview();
});

</script>

<template>
    <!-- スキャン画面 -->
    <div class="com_app_bscan_wrap1">
        <div class="com_app_bscan_nav1">
            <div class="title1">
                発注スキャン
            </div>
            <a href="#" class="prev1 l" @click.prevent="handleBeforeLeave"></a>
            <router-link :to="url.page.orderCart" class="txt1 r">カートへ</router-link>
        </div>
        <div class="com_app_bscan_text1">
            <p class="com_fs16 text-center">
                商品のバーコードをスキャンしてください
            </p>
        </div>
        <!-- カメラエリア -->
        <div
            ref="cameraAreaRef"
            class="com_app_bscan_area1"
            :class="{ 'scan-success-highlight': isScanSuccessHighlight }"
        >
            <video ref="videoRef" class="preview-video" autoplay playsinline muted></video>
            <span v-if="isReading" class="cross1"></span>
            <div v-if="!isReading" class="scan-overlay">
                <p>読み取るボタンで<br>開始します</p>
            </div>
        </div>
        <canvas ref="canvasRef" width="1920" height="1080" style="display: none;"></canvas>
        <p v-if="scanError" class="com_fs12 error" style="white-space: pre-wrap; word-break: break-word; text-align: left;">{{ scanError }}</p>

        <!-- 集計 -->
        <div class="com_app_bscan_data1">
            <div>カート行数: {{ cartTotalRecord }}</div>
            <div>数量合計: {{ cartTotalQuantity }}</div>
        </div>

        <!-- スキャン結果表示（この領域のみスクロール） -->
        <div class="goods-scroll-area">
            <Goods
                v-if="scannedItem"
                :key="scannedItem.janCode"
                :screenId="screenId"
                :isPermittedOrder="true"
                :goods="scannedItem"
                :index="0"
                :goodsTypes="goodsTypes"
                @setOperatingGoodsData="setOperatingGoodsData"
                @addToCart="handleGoodsAddToCart"
                @updateLoading="handleUpdateLoading"
                @apiError="handleApiError"
                @validationError="handleValidationError"
                @quantityChanged="handleQuantityChanged"
            />
        </div>

        <!-- ボタン -->
        <div class="com_app_bscan_buttons1">
            <div>
                <button type="button" class="btn_scan3" @click="startScan">
                    <img src="/front/assets/common/img/btn_scan3.png" alt="読取ボタン">
                </button>

                <a href="#" class="btn1" :class="{ disabled: isAddToCartDisabled }" @click.prevent="!isAddToCartDisabled && tryAddToCart(true)">
                    カートに追加
                    <img src="/front/assets/common/img/icon_cart3.png">
                </a>

                <a href="#" class="btn1" :class="{ disabled: isIdenticalDisabled }" @click.prevent="searchSameIngredients">
                    同一成分検索
                    <img src="/front/assets/common/img/icon_search1.png">
                </a>
            </div>
        </div>
    </div>

    <Transition>
        <div class="loading-animation" v-show="isLoading">
            <div class="show-icon-box">
                <img :src="props.loadingIcon" />
            </div>
        </div>
    </Transition>

    <!-- APIエラーモーダル -->
    <ApiErrorModal :messages="errorMessages" />

    <!-- 同一成分商品検索結果モーダル -->
    <IdenticalFormulationItemAppModal
        :jan-code="identicalFormulationJanCode"
        :goods-types="goodsTypes"
        :identical-formulation-list-api-url="props.identicalFormulationListApiUrl"
        :include-self="true"
        @select-drug="handleSelectDrug"
        @api-error="handleApiError"
        @modal-closed="handleIdenticalModalClosed"
    />

    <!-- スキャン商品確認モーダル -->
    <DeleteFromBarcodeScanConfirmModal 
        @deleteFromBarcodeScan="deleteFromBarcodeScan"
        @cancelDelete="cancelDeleteFromBarcodeScan"
    />

    <!-- 対象商品なしモーダル -->
    <NoTargetGoodsModal />

    <!-- カートに追加確認モーダル -->
    <AddToCartConfirmModal @addToCart="handleConfirmAddToCart" />

    <!-- カートに入れていない確認モーダル -->
    <NoAddCartConfirmModal
        @addToCart="handleAddAndLeave"
        @backPrev="handleLeaveWithoutAdd" />

    <AddToCartAppCompleteModal @hidden="handleCompleteModalHidden" />

    <!-- 取扱中止品残存時の確認モーダル -->
    <DiscontinuedLeaveConfirmModal
        :leaveTo="discontinuedLeaveTo"
        @proceed="handleDiscontinuedProceed" />
</template>

<style scoped>

.preview-video {
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
}

.scan-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(80, 80, 80, 0.75);
    z-index: 2;
}

.scan-overlay p {
    margin: 0;
    color: #fff;
    font-size: 4.8vw;
    font-weight: 700;
    line-height: 1.5;
    text-align: center;
}

:global(body.scan-camera-preview-active),
:global(body.scan-camera-preview-active html),
:global(body.scan-camera-preview-active #app),
:global(body.scan-camera-preview-active #wrapper1){
    background: transparent !important;
    overflow: hidden;
}

:global(body.scan-camera-preview-active .com_app_bscan_nav1),
:global(body.scan-camera-preview-active .com_app_bscan_text1),
:global(body.scan-camera-preview-active .goods-scroll-area),
:global(body.scan-camera-preview-active .com_app_bscan_buttons1 > div) {
    background: #fff !important;
    margin: 0;
}

:global(body.scan-camera-preview-active .com_app_bscan_area1 video) {
    opacity: 0;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>